import { useState } from 'react';

import { PageTextContent } from '@/components/common/PageTextContent';
import { FormContainer } from '@/components/forms/FormContainer/FormContainer';
import { FormError, HubspotForm } from '@/components/forms/HubspotForm';
import { FormLayout } from '@/components/layout/FormLayout';
import { AbFormContainer, AbTestWrapper } from '@/lib/AbTesting';
import { BASE_REVALIDATE } from '@/lib/constants';
import { useDynamicYield } from '@/lib/dynamicYield';
import { getHubspotForm } from '@/lib/HubspotFormApi';
import {
  formQuery,
  localeTranslationsQuery,
  navigationQuery,
  navigations_exQuery,
  siteSettingsQuery,
} from '@/lib/queries';
import { getClient } from '@/lib/sanityUtils';
import { getValidLocale } from '@/lib/utils';

import type {
  SanityAbTestFormType,
  SanityHubspotFormType,
  SanityNavigationType,
} from '@/types/sanity';
import type {
  Navigation_exType,
  SiteSettings,
  Translation,
} from '@/types/shared';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';

const slug = 'employer-contact-form';

export const getStaticProps = (async ({ locale }) => {
  const client = getClient();

  const validLocale = getValidLocale(locale);

  if (!validLocale) {
    return {
      notFound: true,
    };
  }

  const contactForm = await client.fetch<SanityHubspotFormType>(
    formQuery({ slug, locale: validLocale }),
  );

  const siteSettings = await client.fetch<SiteSettings>(
    siteSettingsQuery({ locale: validLocale }),
  );

  const translations = await client.fetch<Translation[] | null>(
    localeTranslationsQuery(contactForm),
  );

  const navigation = await client.fetch<SanityNavigationType>(
    navigationQuery({ locale: validLocale }),
  );
  const navigations_ex = await client.fetch<Navigation_exType[]>(
    navigations_exQuery({ locale: validLocale }),
  );

  if (!contactForm) {
    return {
      notFound: true,
    };
  }

  const formData = await getHubspotForm(contactForm.hubspotFormId);

  return {
    props: {
      contactForm,
      formData,
      locale: validLocale,
      translations,
      siteSettings,
      navigation,
      navigations_ex,
    },
    revalidate: Number(process.env.REVALIDATE || BASE_REVALIDATE),
  };
}) satisfies GetStaticProps;

const ContactForm = ({
  contactForm,
  locale,
  formData,
  translations,
  siteSettings,
  navigation,
  navigations_ex,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const [showFormHeader, setShowFormHeader] = useState(true);

  const { recommendationContext } = useDynamicYield(locale, 'CATEGORY', [
    'CTA page',
  ]);

  const handleOnSubmitForm = () => {
    setShowFormHeader(false);
  };

  const testimonialCard = {
    body: contactForm.testimonialQuote,
    logo: { ...contactForm.testimonialLogo },
    author: contactForm.testimonialName,
    authorRole: contactForm.testimonialRole,
  };

  const abTest = contactForm.abTest as unknown as SanityAbTestFormType;

  return (
    <FormLayout
      SEO={contactForm?.SEO}
      localeTranslations={translations}
      recommendationContext={recommendationContext}
      navigation={navigation}
      navigations_ex={navigations_ex}
      siteSettings={siteSettings}
      contentPane={
        <PageTextContent
          heading={contactForm.heading}
          text={contactForm.body}
          caption={contactForm.caption}
          formHeaderTitle={contactForm.formHeaderTitle ?? null}
          testimonialCard={testimonialCard}
          image={contactForm.image ?? null}
        />
      }
    >
      <FormContainer
        header={
          showFormHeader
            ? {
                title: contactForm.formHeaderTitle,
                description: contactForm.formHeaderDescription,
              }
            : null
        }
      >
        {abTest?.variations ? (
          <AbTestWrapper
            abTest={abTest}
            component={AbFormContainer}
            onSubmit={handleOnSubmitForm}
            formType="contactPage"
          />
        ) : formData?.status === 'error' ? (
          <FormError />
        ) : (
          <HubspotForm
            formData={formData}
            breakpoints={contactForm.breakpoints}
            onSubmit={handleOnSubmitForm}
            formType="contactPage"
          />
        )}
      </FormContainer>
    </FormLayout>
  );
};

export default ContactForm;
